import dayjs from 'date'
import { PathOptions, LeafletMouseEvent } from 'leaflet'
import { Polyline } from 'react-leaflet'
import React, { useCallback, useMemo, useState } from 'react'

import { Text } from 'components/dataDisplay'

import Popup, { PopupData } from '../Popup/Popup'


export type TrackPoint = {
  vel: number
  ts: number
}

type PolylineItemProps = {
  geometry: [ number, number ][]
  color?: string
  pathOptions?: PathOptions
  popupData?: PopupData
  points?: TrackPoint[]
  isFavorite?: boolean
  toggleFavorite?: () => void
}

const findNearestPointIndex = (geometry: [ number, number ][], lat: number, lng: number): number => {
  let nearestIndex = 0
  let minDistance = Infinity

  geometry.forEach(([ pointLat, pointLng ], index) => {
    const distance = (pointLat - lat) ** 2 + (pointLng - lng) ** 2

    if (distance < minDistance) {
      minDistance = distance
      nearestIndex = index
    }
  })

  return nearestIndex
}

const PolylineItem: React.FC<PolylineItemProps> = (props) => {
  const { geometry, color = '#3d75e4', pathOptions, popupData, points, isFavorite, toggleFavorite } = props

  const [ activePoint, setActivePoint ] = useState<TrackPoint>()

  const handleClick = useCallback((event: LeafletMouseEvent) => {
    if (!points?.length) {
      return
    }

    const { lat, lng } = event.latlng
    const nearestIndex = findNearestPointIndex(geometry, lat, lng)

    setActivePoint(points[nearestIndex])
  }, [ geometry, points ])

  const eventHandlers = useMemo(() => ({
    click: handleClick,
  }), [ handleClick ])

  const data = useMemo<PopupData | undefined>(() => {
    if (!popupData) {
      return undefined
    }

    if (!activePoint) {
      return popupData
    }

    return {
      ...popupData,
      children: (
        <>
          <Text
            className="mt-8px px-4px bg-snatch"
            message={`${activePoint.vel} км/ч`}
            size="s10"
            color="titanic"
          />
          <Text
            className="mt-4px px-4px bg-snatch"
            message={dayjs.unix(activePoint.ts).format('DD.MM.YYYY HH:mm')}
            size="s10"
            color="titanic"
          />
        </>
      ),
    } as PopupData
  }, [ popupData, activePoint ])

  return (
    <Polyline
      positions={geometry}
      bubblingMouseEvents={false}
      pathOptions={{
        color,
        weight: 2,
        ...pathOptions,
      }}
      eventHandlers={eventHandlers}
    >
      {
        Boolean(data) && (
          <Popup
            data={data}
            offsetX={0}
            offsetY={0}
            isFavorite={isFavorite}
            toggleFavorite={toggleFavorite}
          />
        )
      }
    </Polyline>
  )
}


export default PolylineItem
